import React, { useState } from "react";
import { Icons } from "./icons.jsx";
import {
  StyledPill,
  StyledActionButton,
  StyledGuruAvatar,
  StarRowRoot,
  StarItemWrap,
  StarFillOverlay,
  StyledLiveDot,
  StyledIconBtn,
  DropdownRoot,
  DropdownTrigger,
  DropdownValueSpan,
  DropdownChevron,
  DropdownBackdrop,
  DropdownMenu,
  DropdownOption,
  DropdownOptionSpacer,
} from "./style.js";

// ── Pill ──────────────────────────────────────────────────────────────────────
const PILL_TONES = {
  default: {
    bg: "rgba(255,255,255,0.06)",
    fg: "#D1C7FF",
    bd: "rgba(255,255,255,0.12)",
  },
  turq: {
    bg: "rgba(1,236,255,0.12)",
    fg: "#01ECFF",
    bd: "rgba(1,236,255,0.30)",
  },
  purple: {
    bg: "rgba(150,46,255,0.16)",
    fg: "#D1C7FF",
    bd: "rgba(150,46,255,0.40)",
  },
  light: {
    bg: "rgba(255,255,255,0.85)",
    fg: "#211F45",
    bd: "rgba(255,255,255,1)",
  },
  success: {
    bg: "rgba(23,178,106,0.15)",
    fg: "#7FE5B0",
    bd: "rgba(23,178,106,0.4)",
  },
  tag: {
    bg: "rgba(150,46,255,0.07)",
    fg: "#C4ABFF",
    bd: "rgba(150,46,255,0.15)",
  },
};

export const Pill = ({ children, tone = "default", size = "sm", style }) => {
  const t = PILL_TONES[tone] || PILL_TONES.default;
  const isSmall = size === "sm";
  return (
    <StyledPill
      $bg={t.bg}
      $fg={t.fg}
      $bd={t.bd}
      $isSmall={isSmall}
      style={style}
    >
      {children}
    </StyledPill>
  );
};

// ── ActionButton ──────────────────────────────────────────────────────────────
const BTN_SIZES = {
  sm: { h: 36, px: 14, fs: 13, gap: 6 },
  md: { h: 44, px: 18, fs: 14, gap: 8 },
  lg: { h: 52, px: 22, fs: 15, gap: 10 },
};
const BTN_VARIANTS = {
  primary: {
    background: "linear-gradient(95deg,#01ECFF 0%,#6FFFFF 100%)",
    color: "#030039",
    border: "1px solid rgba(1,236,255,0.6)",
    boxShadow: "0 0 0 1px rgba(1,236,255,0.25),0 8px 24px rgba(1,236,255,0.25)",
    fontWeight: 600,
  },
  secondary: {
    background: "rgba(255,255,255,0.06)",
    color: "#FEFEFE",
    border: "1px solid rgba(255,255,255,0.16)",
    fontWeight: 500,
  },
  ghost: {
    background: "transparent",
    color: "#D1C7FF",
    border: "1px solid transparent",
    fontWeight: 500,
  },
  purple: {
    background: "linear-gradient(95deg,#962EFF 0%,#B97AFF 100%)",
    color: "#FEFEFE",
    border: "1px solid rgba(150,46,255,0.6)",
    boxShadow:
      "0 0 0 1px rgba(150,46,255,0.2),0 8px 24px rgba(150,46,255,0.25)",
    fontWeight: 600,
  },
  purpleOutline: {
    background: "transparent",
    color: "#FEFEFE",
    border: "1px solid rgba(150,46,255,0.55)",
    fontWeight: 500,
  },
  danger: {
    background: "#FF3B5B",
    color: "#FEFEFE",
    border: "none",
    boxShadow: "0 0 24px rgba(255,59,91,0.4)",
    fontWeight: 600,
  },
};

export const ActionButton = ({
  variant = "primary",
  size = "md",
  icon,
  iconRight,
  children,
  style,
  ...rest
}) => {
  const s = BTN_SIZES[size];
  const v = BTN_VARIANTS[variant] || BTN_VARIANTS.primary;
  return (
    <StyledActionButton
      $h={s.h}
      $px={s.px}
      $fs={s.fs}
      $gap={s.gap}
      $background={v.background}
      $color={v.color}
      $border={v.border}
      $boxShadow={v.boxShadow}
      $fontWeight={v.fontWeight}
      style={style}
      {...rest}
    >
      {icon}
      {children}
      {iconRight}
    </StyledActionButton>
  );
};

// ── GuruAvatar ────────────────────────────────────────────────────────────────
export const GuruAvatar = ({ src, name, size = 40, ring }) => (
  <StyledGuruAvatar $size={size} $src={src} $ring={ring} title={name}>
    {!src &&
      name &&
      name
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")}
  </StyledGuruAvatar>
);

// ── StarRow ───────────────────────────────────────────────────────────────────
export const StarRow = ({ value, size = 14 }) => (
  <StarRowRoot>
    {[1, 2, 3, 4, 5].map((i) => {
      const filled = value >= i - 0.25;
      const half = !filled && value >= i - 0.75;
      return (
        <StarItemWrap key={i}>
          <Icons.starOut
            size={size}
            style={{ color: "rgba(255,255,255,0.18)" }}
          />
          {(filled || half) && (
            <StarFillOverlay $half={half}>
              <Icons.star size={size} />
            </StarFillOverlay>
          )}
        </StarItemWrap>
      );
    })}
  </StarRowRoot>
);

// ── LiveDot ───────────────────────────────────────────────────────────────────
export const LiveDot = () => <StyledLiveDot />;

// ── IconBtn ───────────────────────────────────────────────────────────────────
export const IconBtn = ({ children, style, ...rest }) => (
  <StyledIconBtn style={style} {...rest}>
    {children}
  </StyledIconBtn>
);

// ── Dropdown ──────────────────────────────────────────────────────────────────
export const Dropdown = ({ value, onChange, options, icon, iconOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [trigHover, setTrigHover] = useState(false);
  const [optHover, setOptHover] = useState(null);
  const isDefault = value === options[0];

  return (
    <DropdownRoot>
      <DropdownTrigger
        $open={open}
        $trigHover={trigHover}
        $iconOnly={iconOnly}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setTrigHover(true)}
        onMouseLeave={() => setTrigHover(false)}
      >
        {iconOnly ? (
          (icon ?? (
            <Icons.filter
              size={16}
              style={{ color: open ? "#A87EFF" : "#D1C7FF" }}
            />
          ))
        ) : (
          <>
            {icon}
            <DropdownValueSpan $isDefault={isDefault}>
              {value}
            </DropdownValueSpan>
            <DropdownChevron $open={open}>
              <Icons.chevDown size={14} />
            </DropdownChevron>
          </>
        )}
      </DropdownTrigger>
      {open && (
        <>
          <DropdownBackdrop onClick={() => setOpen(false)} />
          <DropdownMenu>
            {options.map((o, i) => {
              const isSelected = o === value;
              const isHovered = optHover === i;
              return (
                <DropdownOption
                  key={o}
                  $isSelected={isSelected}
                  $isHovered={isHovered}
                  onMouseEnter={() => setOptHover(i)}
                  onMouseLeave={() => setOptHover(null)}
                  onClick={() => {
                    onChange(o);
                    setOpen(false);
                  }}
                >
                  {isSelected ? (
                    <Icons.check
                      size={14}
                      style={{ color: "#962EFF", flexShrink: 0 }}
                    />
                  ) : (
                    <DropdownOptionSpacer />
                  )}
                  <span>{o}</span>
                </DropdownOption>
              );
            })}
          </DropdownMenu>
        </>
      )}
    </DropdownRoot>
  );
};
